import React, { useState, useEffect } from "react"
import styles from "./Wikis.module.scss"
import { UserInterface } from "../../App"
import { API_ROUTE } from "../../utils"
import axios from "axios"
import toast from "react-hot-toast"

interface WikiPage {
    _id: string
    title: string
}

interface WikiInterface {
    _id: string
    created: string
    lastModified: string
    pages: WikiPage[] // normalize to array (backend is an array)
    name: string
    orgId: string
}

const Wikis: React.FC<{
    user: UserInterface
    setUser: React.Dispatch<React.SetStateAction<UserInterface>>
    newAppSpace: (appSpace: object) => void
    setOrgWikis: React.Dispatch<React.SetStateAction<any>>
    activeAppSpace?: {
        _id: string
        fileId: string
        content: string
        contentType: string
    }
}> = ({ user, newAppSpace, setOrgWikis, activeAppSpace }) => {
    const [noWiki, setNoWiki] = useState(false)
    const [wikiData, setWikiData] = useState<WikiInterface | null>(null)
    const [busyId, setBusyId] = useState<string | null>(null)

    const [searchQuery, setSearchQuery] = useState("")

    const fetchWiki = async () => {
        try {
            const response = await axios.post(`${API_ROUTE}/wikis/get`, {
                userId: user._id,
                authToken: user.authToken,
                orgId: user.activeOrgId,
            })

            const data = response.data
            if (!data.success) {
                throw new Error(data.message || "Failed to fetch wiki")
            } else {
                // Ensure pages is an array
                const pages: WikiPage[] = Array.isArray(data.pages)
                    ? data.pages
                    : Object.values(data.pages || {})
                const normalized = { ...data, pages }
                setWikiData(normalized)
                setOrgWikis(normalized)
                setNoWiki(false)
            }
        } catch (err) {
            if (axios.isAxiosError(err) && err.response?.status === 404) {
                // 404, allow user to create new wiki
                setNoWiki(true)
                setWikiData(null)
            } else {
                console.error("Error fetching wiki:", err)
                toast.error("Something went wrong")
            }
        }
    }

    const createWiki = async () => {
        try {
            const response = await axios.post(`${API_ROUTE}/wikis/create`, {
                userId: user._id,
                authToken: user.authToken,
                orgId: user.activeOrgId,
            })
            const data = response.data
            if (!data.success) {
                throw new Error(data.message || "Failed to create wiki")
            }
            await fetchWiki()
        } catch (err) {
            toast.error((err as Error).message)
        }
    }

    useEffect(() => {
        if (user.activeOrgId) {
            fetchWiki()
        } else {
            toast.error("No active organization selected")
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user.activeOrgId])

    async function newWikiPage() {
        try {
            const response = await axios.post(`${API_ROUTE}/wikis/new-page`, {
                userId: user._id,
                authToken: user.authToken,
                orgId: user.activeOrgId,
                page: { title: "New Page", content: "" },
            })
            const data = response.data
            if (!data.success) throw new Error(data.message || "Failed to create new page")
            await fetchWiki()
        } catch (err) {
            toast.error((err as Error).message)
        }
    }

    async function deletePage(pageId: string, pageTitle: string) {
        try {
            const ok = window.confirm(`Delete "${pageTitle}"? This cannot be undone.`)
            if (!ok) return
            setBusyId(pageId)
            await axios.post(`${API_ROUTE}/wikis/delete-page`, {
                userId: user._id,
                authToken: user.authToken,
                orgId: user.activeOrgId,
                pageId,
            })
            toast.success("Page deleted")
            await fetchWiki()
        } catch (err) {
            toast.error((err as any)?.response?.data?.message || (err as Error).message)
        } finally {
            setBusyId(null)
        }
    }

    async function renamePage(pageId: string, currentTitle: string) {
        try {
            const newTitle = window.prompt("Rename page to:", currentTitle)
            if (!newTitle || newTitle.trim() === "" || newTitle === currentTitle) return
            setBusyId(pageId)
            await axios.post(`${API_ROUTE}/wikis/rename-page`, {
                userId: user._id,
                authToken: user.authToken,
                orgId: user.activeOrgId,
                pageId,
                title: newTitle.trim(),
            })
            toast.success("Page renamed")
            await fetchWiki()
        } catch (err) {
            toast.error((err as any)?.response?.data?.message || (err as Error).message)
        } finally {
            setBusyId(null)
        }
    }

    const filteredPages = (wikiData?.pages || []).filter((p) => {
        const q = searchQuery.trim().toLowerCase()
        if (!q) return true
        return (p.title || "").toLowerCase().includes(q)
    })

    return (
        <div className={styles.Wikis}>
            {noWiki ? (
                <div className={styles.newWikiContainer}>
                    <h2 className={styles.heading}>
                        {wikiData?.name}
                        {wikiData?.name && <span>/</span>}
                        Wikis
                    </h2>

                    <div className={styles.newWiki}>
                        <p>No Wiki found for this organization. Create one?</p>
                        <button onClick={createWiki}>Create New Wiki</button>
                    </div>
                </div>
            ) : (
                <div className={styles.wikiContainer}>
                    <div className={styles.sidebar}>
                        <h2 className={styles.heading}>
                            {wikiData?.name}
                            {wikiData?.name && <span>/</span>}
                            Wikis
                        </h2>
                        <div className={styles.pages}>
                            <input
                                type="text"
                                placeholder="Search pages…"
                                value={searchQuery}
                                className={styles.search}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />

                            <div onClick={newWikiPage} className={styles.page}>
                                <i className="material-symbols-rounded">note_add</i>
                                New Page
                            </div>

                            <div className={styles.level}>
                                {filteredPages.map((page) => (
                                    <div
                                        key={page._id}
                                        className={styles.page}
                                        onClick={async () => {
                                            try {
                                                const response = await axios.get(
                                                    `${API_ROUTE}/files/get-file/${page._id}`
                                                )
                                                if (!response.data._id) {
                                                    toast.error("Page not found")
                                                } else {
                                                    newAppSpace({
                                                        ...response.data,
                                                        fileId: response.data._id,
                                                        content: response.data.content,
                                                        contentType: response.data.contentType,
                                                    })
                                                }
                                            } catch (error) {
                                                console.error("Error fetching page:", error)
                                                toast.error("Failed to fetch page")
                                            }
                                        }}>
                                        <i className="material-symbols-rounded">draft</i>
                                        <span
                                            style={{
                                                flex: 1,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            }}>
                                            {page.title}
                                        </span>

                                        <i
                                            title="Rename"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                if (!busyId) renamePage(page._id, page.title)
                                            }}
                                            className="material-symbols-rounded"
                                            style={{ opacity: busyId === page._id ? 0.4 : 1 }}>
                                            edit
                                        </i>

                                        <i
                                            title="Delete"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                if (!busyId) deletePage(page._id, page.title)
                                            }}
                                            className="material-symbols-rounded"
                                            style={{ opacity: busyId === page._id ? 0.4 : 1 }}>
                                            delete
                                        </i>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {!activeAppSpace?._id && (
                        <div className={styles.nothing}>
                            <i className="material-symbols-rounded">add_notes</i>
                            Open or create a new wiki page
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default Wikis
